import { useMemo, type ReactNode } from "react";
import { IntlProvider, useIntl } from "react-intl";
import { en, type MessageId } from "./en";

export type { MessageId };

const CATALOGUES: Record<string, Record<MessageId, string>> = { en };

/** The messages with the product's name where they say Swaralo (a reseller's brand, V1-E14-T03). */
export function branded(messages: Record<MessageId, string>, product: string | undefined): Record<MessageId, string> {
  if (!product || product === "Swaralo") return messages;
  return Object.fromEntries(
    Object.entries(messages).map(([id, text]) => [id, text.replaceAll("Swaralo", product)]),
  ) as Record<MessageId, string>;
}

export function I18nProvider({ locale = "en", product, children }: {
  locale?: string;
  product?: string;
  children: ReactNode;
}) {
  const messages = useMemo(() => branded(CATALOGUES[locale] ?? en, product), [locale, product]);
  return (
    <IntlProvider locale={locale} defaultLocale="en" messages={messages}>
      {children}
    </IntlProvider>
  );
}

type Values = Record<string, string | number>;

/** A message by id, typed: an id that is not in the catalogue does not compile. */
export function useT(): (id: MessageId, values?: Values) => string {
  const intl = useIntl();
  return (id, values) => intl.formatMessage({ id }, values);
}

/** Dates, times and durations in the reader's locale. */
export function useFormat() {
  const intl = useIntl();
  return {
    dateTime: (iso: string) => intl.formatDate(iso, { dateStyle: "medium", timeStyle: "short" }),
    date: (iso: string) => intl.formatDate(iso, { dateStyle: "medium" }),
    time: (iso: string) => intl.formatTime(iso, { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    money: (amount: number, currency: string) =>
      intl.formatNumber(amount, { style: "currency", currency, maximumFractionDigits: 4 }),
    number: (n: number) => intl.formatNumber(n),
    /** A file's size: 340 kB, 2.1 MB. */
    bytes: (n: number) => {
      const [value, unit] = n < 1e6 ? [n / 1e3, "kilobyte"] : [n / 1e6, "megabyte"];
      return intl.formatNumber(value, { style: "unit", unit, unitDisplay: "short", maximumFractionDigits: 1 });
    },
    duration: (ms: number) => {
      const s = Math.round(ms / 1000);
      return s < 60
        ? intl.formatMessage({ id: "format.seconds" }, { s })
        : intl.formatMessage({ id: "format.minutes" }, { m: Math.floor(s / 60), s: s % 60 });
    },
  };
}
